import {createReducer, on} from "@ngrx/store";
import {SharedIssueBaseState} from "@shared/components/issues/store/shared-issue-base.state";
import {SharedIssueBaseActions} from "@shared/components/issues/store/shared-issue-base.action-type";
import {IssueContentType, IssueType} from "@server-models";
import {CoreEnvironmentService} from "@core/services/environment/core-environment.service";
import {ILocalIssueDetailData} from "@shared/components/issues/interfaces/local-issue-detail-data.interface";

function getInitialSharedIssueState(): SharedIssueBaseState {
  const env = CoreEnvironmentService.getEnvironment();
  return {
    issueList: {
      items: [],
      filter: [],
      paging: {
        totalItems: 0,
        pageNumber: 0,
        pageSize: env?.apiUrl?.pageDefaultSize || 50,
        totalPages: 1
      },
    },
    issueTemplateList: {
      items: [],
      filter: [],
      paging: {
        totalItems: 0,
        pageNumber: 0,
        pageSize: env?.apiUrl?.pageDefaultSize || 50,
        totalPages: 1
      }
    },
    currentIssue: {},
    isNavigationRequired: true,
    currentIssueMetaInfo: {
      issueId: NaN,
      issueType: <IssueType>{}
    },
    currentIssueTemplate: {
      templateId: NaN,
      contentType: <IssueContentType>{},
      preview: {}
    },
    shortCutTemplate: {
      issueTemplateId: NaN,
      contentType: <IssueContentType>{}
    },
    selectedPicture: {},
    selectedPictureIsLoading: false,
    selectedPictureIsLoadingId: NaN,
    selectedPictureAsBlob: new Blob(),
    savedFile: {
      pictureId: NaN,
      fileId: NaN,
      path: ''
    },
    selectedSignatureAsBlob: new Blob(),
    selectedSignatureIsLoading: false,
    selectedSignatureIsLoadingId: NaN,
    savedSignature: {
      id: NaN,
      dataUrl: '',
      index: NaN
    },
    detailDataFormatted: <ILocalIssueDetailData>{},
    isSubmitLoading: false,
    isLoading: true,
    isLoadingId: NaN
  }
}

export const SharedIssueBaseStore = createReducer(
  getInitialSharedIssueState(),
  on(SharedIssueBaseActions.getIssueListPaginated, (state) => ({
    ...state,
    isLoading: true
  })),
  on(SharedIssueBaseActions.getIssueListPaginatedSuccess, (state, { data }) => ({
    ...state,
    // Important! include the current state.items and the data.items to create a new array with the
    // existing ones and the new one that is requested
    issueList: {
      ...state.issueList,
      items: [...state.issueList.items, ...data.items],
      paging: data.paging,
    },
    isLoading: false
  })),
  on(SharedIssueBaseActions.getIssueListPaginatedRefresh, (state, { data }) => ({
    ...state,
    issueList: {
      ...state.issueList,
      items: [...data.items],
      paging: data.paging,
    },
    isLoading: false
  })),
  on(SharedIssueBaseActions.getIssueListPaginatedCancel, (state) => ({
    ...state,
    isLoading: false
  })),
  on(SharedIssueBaseActions.getIssueListPaginatedFail, (state) => ({
    ...state,
    isLoading: false
  })),
  on(SharedIssueBaseActions.getIssueTemplateList, (state) => ({
    ...state,
    isLoading: true
  })),
  on(SharedIssueBaseActions.getIssueTemplateListSuccess, (state, { data }) => ({
    ...state,
    issueTemplateList: {
      ...state.issueTemplateList,
      items: data.paging.pageNumber <= 1 ? [...data.items] : [...state.issueTemplateList.items, ...data.items],
      paging: data.paging,
    },
    isLoading: true
  })),
  on(SharedIssueBaseActions.getIssueTemplateListDone, (state) => ({
    ...state,
    isLoading: false
  })),
  on(SharedIssueBaseActions.getIssueTemplateListFail, (state) => ({
    ...state,
    isLoading: false
  })),
  on(SharedIssueBaseActions.getIssuePreview, (state, { templateId, contentType }) => ({
    ...state,
    shortCutTemplate: {
      issueTemplateId: templateId,
      contentType
    },
    currentIssueTemplate: {
      ...state.currentIssueTemplate,
      templateId,
    },
    isLoading: true
  })),
  on(SharedIssueBaseActions.getPreview, (state) => ({
    ...state,
    isLoading: true
  })),
  on(SharedIssueBaseActions.getIssuePreviewSuccess, (state, { data, templateId, contentType, resourceId }) => ({
    ...state,
    shortCutTemplate: {
      issueTemplateId: templateId,
      contentType
    },
    currentIssueTemplate: {
      ...state.currentIssueTemplate,
      templateId,
      resourceId,
      contentType,
      preview: data
    },
    isLoading: false
  })),
  on(SharedIssueBaseActions.getIssuePreviewDone, (state) => ({
    ...state,
    isLoading: false
  })),
  on(SharedIssueBaseActions.getIssuePreviewFail, (state) => ({
    ...state,
    currentIssueTemplate: {
      templateId: NaN,
      contentType: <IssueContentType>{},
      preview: {}
    },
    shortCutTemplate: {
      issueTemplateId: NaN,
      contentType: <IssueContentType>{}
    },
    isLoading: false
  })),
  on(SharedIssueBaseActions.getById, (state) => ({
    ...state,
    isLoading: true
  })),
  on(SharedIssueBaseActions.getIssueByIssueType, (state, { issueId, issueType }) => ({
    ...state,
    currentIssueMetaInfo: {
      issueId,
      issueType
    },
    isLoading: true
  })),
  on(SharedIssueBaseActions.getByIdSuccess, (state, { data }) => ({
    ...state,
    currentIssue: data,
    isLoading: false
  })),
  on(SharedIssueBaseActions.getByIdFail, (state) => ({
    ...state,
    isLoading: false
  })),
  on(SharedIssueBaseActions.prepareFormsToSendTemplatePreview, (state) => ({
    ...state,
    isSubmitLoading: true,
  })),
  on(SharedIssueBaseActions.sendTemplatePreview, (state) => ({
    ...state,
    isSubmitLoading: true,
  })),
  on(SharedIssueBaseActions.sendTemplatePreviewSuccess, (state) => ({
    ...state,
    savedFile: getInitialSharedIssueState().savedFile,
    selectedPicture: getInitialSharedIssueState().selectedPicture,
    selectedPictureAsBlob: getInitialSharedIssueState().selectedPictureAsBlob,
    isSubmitLoading: false,
  })),
  on(SharedIssueBaseActions.sendTemplatePreviewFail, (state) => ({
    ...state,
    isSubmitLoading: false,
  })),
  on(SharedIssueBaseActions.formatReadDetailDataFinish, (state, { detailDataFormatted }) => ({
    ...state,
    detailDataFormatted,
    isLoading: false
  })),
  on(SharedIssueBaseActions.askPictureInput, (state, { isLoadingId }) => ({
    ...state,
    selectedPictureIsLoading: true,
    selectedPictureIsLoadingId: isLoadingId
  })),
  on(SharedIssueBaseActions.askPictureInputSuccess, (state, { picture, pictureId }) => ({
    ...state,
    selectedPicture: {
      picture,
      pictureId
    },
    selectedPictureIsLoading: false
  })),
  on(SharedIssueBaseActions.askPictureInputFail, (state) => ({
    ...state,
    selectedPictureIsLoading: false
  })),
  on(SharedIssueBaseActions.reducePictureSuccess, (state, { blob }) => ({
    ...state,
    selectedPictureAsBlob: blob
  })),
  on(SharedIssueBaseActions.reducePictureError, (state,) => ({
    ...state,
    selectedPictureIsLoading: false
  })),
  on(SharedIssueBaseActions.savePictureSuccess, (state, { pictureId, fileId, path }) => ({
    ...state,
    savedFile: {
      pictureId,
      fileId,
      path
    },
    selectedPictureIsLoading: false
  })),
  on(SharedIssueBaseActions.savePictureFail, (state) => ({
    ...state,
    selectedPictureIsLoading: false
  })),
  on(SharedIssueBaseActions.savedPictureClear, (state) => ({
    ...state,
    savedFile: getInitialSharedIssueState().savedFile,
    selectedPicture: getInitialSharedIssueState().selectedPicture,
    selectedPictureAsBlob: getInitialSharedIssueState().selectedPictureAsBlob,
  })),
  on(SharedIssueBaseActions.convertDateURLToBlob, (state, { isLoadingId }) => ({
    ...state,
    selectedSignatureIsLoading: true,
    selectedSignatureIsLoadingId: isLoadingId
  })),
  on(SharedIssueBaseActions.convertDateURLToBlobSuccess, (state, { blob }) => ({
    ...state,
    selectedSignatureAsBlob: blob,
  })),
  on(SharedIssueBaseActions.convertDateURLToBlobFail, (state,) => ({
    ...state,
    selectedSignatureIsLoading: false
  })),
  on(SharedIssueBaseActions.saveSignatureSuccess, (state, { id, dataUrl, index }) => ({
    ...state,
    savedSignature: {
      id,
      dataUrl,
      index
    },
    selectedSignatureIsLoading: false
  })),
  on(SharedIssueBaseActions.saveSignatureFail, (state) => ({
    ...state,
    selectedSignatureIsLoading: false
  })),
  on(SharedIssueBaseActions.savedSignatureClear, (state) => ({
    ...state,
    savedSignature: getInitialSharedIssueState().savedSignature,
    selectedSignatureAsBlob: new Blob(),
  }))
);
